import React from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import { ReactComponent as MapIcon } from "../../../Assets/Icons/MapIcon.svg";
import { ReactComponent as GenderIcon } from "../../../Assets/Icons/gender.svg";
import { ReactComponent as AgeIcon } from "../../../Assets/Icons/ageIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../Assets/Icons/phoneIcon.svg";
import { ReactComponent as CurrencyIcon } from "../../../Assets/Icons/currency.svg";
import { ReactComponent as CalendarIcon } from "../../../Assets/Icons/Calender.svg";
import { ReactComponent as PostalCodeIcon } from "../../../Assets/Icons/postalcodeicon.svg";
import { ReactComponent as StateIcon } from "../../../Assets/Icons/statesicon.svg";
import { ReactComponent as SuburbIcon } from "../../../Assets/Icons/suburbicon.svg";
import PersonBlue from "../../../Assets/Images/PersonBlue.svg";
import "../../common/OfferList/offerList.scss";
import { Dropdown, Tooltip } from "antd";
import {
  deleteUser,
  updateUserStatus
} from "../../../services/adminService";
import toast from "../../../utils/toast";

const PopupContent = ({ memberDetails, onRedeemedOfferClick, onStatusChange, onStatusChangeRequest, onDelete }) => {
  const dispatch = useDispatch();
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);

  if (!memberDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="loader"></div>
        <p className="mt-4 text-[#767676] font-poppins text-[15px]">Loading member details...</p>
      </div>
    );
  }

  const {
    age,
    email,
    gender,
    phone,
    name,
    redeemedOffers,
    _id,
    status,
    subscriptionStatus,
    subscriptionAmount,
    referredBy,
    createdAt,
    suburb,
    state,
    postalCode,
  } = memberDetails;

  const resolvedPostalCode =
    postalCode ??
    memberDetails?.postcode ??
    memberDetails?.postCode ??
    memberDetails?.zipCode ??
    memberDetails?.zip ??
    "";
  const hasPostalCode = String(resolvedPostalCode || "").trim() !== "";

  // Check if member is deleted
  const isDeleted = memberDetails?.isDeleted === true || String(status || "").toLowerCase() === "deleted";
  const statusLower = String(status || "").toLowerCase();
  const isLive = statusLower === "approved" || statusLower === "live";
  const renderMemberChevron = () => (
    <svg width="7" height="4" viewBox="0 0 10 6" fill="none">
      <path
        d="M1 1.5L5 5.5L9 1.5"
        stroke="#303033"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const getSubscriptionBadgeMeta = (subscriptionValue) => {
    const normalized = String(subscriptionValue || "").toLowerCase();

    if (normalized === "active" || subscriptionValue === true) {
      return {
        label: "Active",
        bgColor: "rgba(80, 205, 137, 0.15)",
        borderColor: "#50CD89",
        dotColor: "#50CD89",
        icon: (
          <svg width="10" height="10" viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
            <path d="M12 13.6667L14.3333 16L19 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      };
    }

    if (normalized === "not_subscribed") {
      return {
        label: "Not Subscribed",
        bgColor: "rgba(140, 140, 140, 0.12)",
        borderColor: "#8C8C8C",
        dotColor: "#8C8C8C",
        icon: (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" style={{ display: "block" }}>
            <path d="M7 12h10" />
          </svg>
        ),
      };
    }

    return {
      label: "Expired",
      bgColor: "rgba(236, 130, 110, 0.15)",
      borderColor: "#EC826E",
      dotColor: "#EC826E",
      icon: (
        <svg width="10" height="10" viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
          <path d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z" fill="white" />
        </svg>
      ),
    };
  };

  const handleStatusChange = async (newStatus) => {
    if (typeof onStatusChangeRequest === "function") {
      onStatusChangeRequest(_id, newStatus, memberDetails);
      return;
    }

    try {
      const action =
        String(newStatus).toLowerCase() === "live"
          ? updateUserStatus({ id: _id, status: newStatus })
          : deleteUser({ id: _id, status: newStatus });
      const result = await dispatch(action).unwrap();
      if (result) {
        if (onStatusChange) onStatusChange();
        const statusLabel =
          String(newStatus).toLowerCase() === "disabled" ? "Disabled" :
          String(newStatus).toLowerCase() === "deleted" ? "Deleted" :
          "Live";
        toast.success(
          `Member status changed to ${statusLabel} successfully!`
        );
      }
    } catch (error) {
      console.error("Failed to update member status:", error);
      toast.error(`Failed to change member status to ${newStatus}.`);
    }
  };

  const handleDeleteMember = () => {
    if (onDelete) {
      onDelete(_id);
    }
  };

  const truncateMemberName = (value, maxLength = 18) => {
    if (!value) return "John Smith";
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  return (
    <div
      style={{ scrollbarWidth: "none" }}
      className="max-h-[600px] overflow-y-auto w-full flex flex-col gap-4 font-poppins"
    >
      {/* Header Section with Avatar and Status */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center">
            <img
              src={PersonBlue}
              alt="Profile Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <div className="flex flex-col items-start text-left leading-tight overflow-hidden">
            <h1
              className="font-semibold text-lg text-gray-900 truncate max-w-[250px] sm:max-w-[400px]"
              title={name || "John Smith"}
            >
              {truncateMemberName(name)}
            </h1>
            <p className="text-sm text-gray-500 truncate max-w-[250px] sm:max-w-[400px]">
              {email || "jsmith@email.com"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
          {/* Subscription Status */}
          <div className="flex items-center gap-2 w-full justify-end">
            <span className="text-[11px] text-gray-400 font-poppins w-[88px] text-left">Sub. Status:</span>
            {(() => {
              const subscriptionBadge = getSubscriptionBadgeMeta(subscriptionStatus);

              return (
                <span className="inline-flex">
                  <Tooltip
                    title={subscriptionBadge.label}
                    arrow
                    placement="top"
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 10050 }}
                  >
                    <div
                      className="flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none"
                      style={{
                        backgroundColor: subscriptionBadge.bgColor,
                        borderColor: subscriptionBadge.borderColor,
                        width: "112px"
                      }}
                    >
                      <div
                        className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: subscriptionBadge.dotColor }}
                      >
                        {subscriptionBadge.icon}
                      </div>
                      <span className="text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1 text-left">
                        {subscriptionBadge.label}
                      </span>
                    </div>
                  </Tooltip>
                </span>
              );
            })()}
          </div>

          {/* Member Status */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-400 font-poppins">Member Status:</span>
            <Dropdown
              menu={{
                items: [
                  !isLive && !isDeleted && {
                    key: "live",
                    label: (
                      <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                        <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#50CD89]">
                          <svg width="10" height="10" viewBox="8 6 14 14" fill="none">
                            <path d="M12 13.6667L14.3333 16L19 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <span className="text-[13px] font-[400] font-poppins text-[#303033]">Live</span>
                      </div>
                    ),
                    onClick: () => handleStatusChange("live")
                  },
                  isLive && {
                    key: "disabled",
                    label: (
                      <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                        <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round">
                            <path d="M7 12h10" />
                          </svg>
                        </div>
                        <span className="text-[13px] font-[400] font-poppins text-[#303033]">Disable</span>
                      </div>
                    ),
                    onClick: () => handleStatusChange("disabled")
                  }
                ].filter(Boolean)
              }}
              trigger={["click"]}
              placement="bottomLeft"
              getPopupContainer={(triggerNode) => triggerNode?.parentNode || document.body}
              overlayStyle={{ zIndex: 100000 }}
              disabled={isDeleted}
              open={statusDropdownOpen}
              onOpenChange={(nextOpen) => setStatusDropdownOpen(nextOpen)}
            >
              <span className="inline-flex">
                <Tooltip
                  title={statusDropdownOpen ? null : (isLive ? "Live" : isDeleted ? "Deleted" : "Disabled")}
                  arrow
                  placement="top"
                  getPopupContainer={() => document.body}
                  overlayStyle={{ zIndex: 10050 }}
                >
                  <button
                    type="button"
                    className="cursor-pointer flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: isLive ? "rgba(80, 205, 137, 0.15)" : "rgba(236, 130, 110, 0.15)",
                      borderColor: isLive ? "#50CD89" : "#EC826E",
                      width: "98px",
                      cursor: "pointer"
                    }}
                  >
                    <div
                      className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: isLive ? "#50CD89" : "#EC826E"
                      }}
                    >
                      {isLive ? (
                        <svg width="12" height="12" viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
                          <path d="M12 13.6667L14.3333 16L19 11" stroke="white" strokeWidth="2.0" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : isDeleted ? (
                        <svg width="12" height="12" viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
                          <path d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z" fill="white" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" style={{ display: "block" }}>
                          <path d="M7 12h10" />
                        </svg>
                      )}
                    </div>
                    <span className="text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1 capitalize text-left">
                      {isLive ? "Live" : isDeleted ? "Deleted" : "Disabled"}
                    </span>
                    {!isDeleted && (
                      <div className="ml-1.5 flex items-center flex-shrink-0">
                        {renderMemberChevron()}
                      </div>
                    )}
                  </button>
                </Tooltip>
              </span>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* User Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-4 mb-2 items-center">
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <AgeIcon />
          <span className="text-xs">{age || "N/A"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <PhoneIcon />
          <span className="text-xs">{phone || "N/A"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676] sm:ml-0 md:ml-0">
          <CalendarIcon />
          <span className="text-xs">{createdAt ? formatDate(createdAt) : "N/A"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <GenderIcon />
          <span className="text-xs">{gender || "N/A"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <CurrencyIcon />
          <span className="text-xs">{subscriptionAmount ? `$${subscriptionAmount}` : "$0"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <SuburbIcon className="w-6 h-6 text-[#00ADEF] flex-shrink-0" />
          <span className="text-xs">{suburb || "N/A"}</span>
        </div>
        <div className="flex items-center gap-3 text-[14px] text-[#767676]">
          <StateIcon className="w-6 h-6 text-[#00ADEF] flex-shrink-0" />
          <span className="text-xs">{state || "N/A"}</span>
        </div>
        {hasPostalCode && (
          <div className="flex items-center gap-3 text-[14px] text-[#767676]">
            <PostalCodeIcon className="w-6 h-6 text-[#00ADEF] flex-shrink-0" />
            <span className="text-xs">{resolvedPostalCode}</span>
          </div>
        )}
        {referredBy ? (
          <div className="flex items-center gap-3 text-[14px] text-[#767676] sm:ml-0 md:ml-0">
            {referredBy?.fundraiserLogo ? (
              <img
                src={process.env.REACT_APP_S3_URL + referredBy?.fundraiserLogo}
                className="w-6 h-6 object-contain rounded-full"
                alt="Fundraiser Logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-xs text-gray-500">?</span>
              </div>
            )}
            <span className="text-xs truncate max-w-[100px]" title={referredBy?.name}>
              {referredBy?.name || 'Unknown Fundraiser'}
            </span>
          </div>
        ) : (
          <div className="hidden sm:block"></div>
        )}
      </div>
      {memberDetails.address && (
        <div className="flex items-center gap-3 text-[14px] text-[#767676] mb-4">
          <MapIcon className="w-4 h-4 text-gray-400" />
          <span className="text-xs">{memberDetails.address}</span>
        </div>
      )}

      {/* Redeemed Offers Section */}
      {redeemedOffers?.length > 0 && (
        <>
          <h2 className="text-[16px] font-[500] text-[#000000] text-start">
            Redeemed Offers
          </h2>
          <div
            className="border border-[#00000029] rounded-[12px] p-4 max-h-[400px] overflow-y-auto"
            style={{ scrollbarWidth: "none" }}
          >
            {/* Flatten the array to render appropriately based on screen size via grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {redeemedOffers.map((offer, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between p-2 cursor-pointer border-b border-[#00000014] last:border-0 sm:border-0"
                  onClick={() => onRedeemedOfferClick && onRedeemedOfferClick(offer)}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        offer?.merchant?.logo
                          ? process.env.REACT_APP_S3_URL +
                          offer?.merchant?.logo
                          : PersonBlue
                      }
                      alt={"Brand"}
                      className="w-11 h-11 rounded-lg object-cover"
                    />
                    <div className="flex flex-col justify-start items-start">
                      <p className="font-medium text-gray-900 text-sm">
                        {offer?.merchant?.businessName}
                      </p>
                      <p className="text-xs text-gray-500 text-left">
                        {offer?.merchant?.address}
                      </p>
                    </div>
                  </div>
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xs font-semibold">
                      {offer?.offers?.length}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Footer Actions */}
      {!isDeleted && (
        <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleDeleteMember}
            className="flex items-center gap-2 px-4 py-2 border border-[#EC826E] text-[#EC826E] rounded-md hover:bg-[#EC826E10] transition-colors"
          >
            <DeleteIcon className="w-4 h-4" />
            <span className="font-medium text-[13px]">Delete Member</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PopupContent;
